import { useState, useEffect } from "react";
import { Box, Typography, Link, Stack, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { IconButtonOutlined } from "./utils/IconButtonOutlined";
import { ReactComponent as IconChevronLeft } from "../../assets/IconChevronLeft.svg";
import { ReactComponent as IconChevronRight } from "../../assets/IconChevronRight.svg";
import { generateGtmEvent } from "../../Helpers";

const padY = 48;
const padX = 52;
const triangleLength = 16;
const tooltipWidth = 388;
const tooltipHeight = 275;

const useStyles = makeStyles((theme) => ({
  tooltip: (props: GuidedTourTooltipProps) => ({
    backgroundColor: theme.palette.text.primary,
    color: "white",
    borderRadius: 15,
    padding: "1.5rem",
    width: tooltipWidth,
    height: props.tooltipHeight || tooltipHeight,
    boxSizing: "border-box",
    boxShadow: `inset 0px 0px 0px 2px ${theme.palette.divider}`,
    zIndex: 3500,
  }),
  outlinedButton: {
    backgroundColor: "transparent",
    borderColor: "white",
    color: "white",
    fontSize: 16,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      borderColor: "white",
      color: "white",
    },
  },
  skipLink: {
    color: "white",
    textDecorationColor: "white",
    "&:hover": {
      color: "white",
      textDecoration: "none",
    },
    "&:focus": {
      outline: "2px solid white",
    },
  },
  triangle: {},
  triangleup: {
    content: '""',
    display: "block",
    position: "absolute",
    top: 0,
    left: 0,
    width: triangleLength,
    height: triangleLength,
    backgroundColor: theme.palette.text.primary,
    boxShadow: `inset 4px 4px 0px -2px ${theme.palette.divider}`,

    transform: "translateY(-50%) translateX(-50%) rotate(45deg)",
    zIndex: 0,
  },
  triangledown: {
    content: '""',
    display: "block",
    position: "absolute",
    top: 0,
    left: 0,
    width: triangleLength,
    height: triangleLength,
    backgroundColor: theme.palette.text.primary,
    boxShadow: `inset -4px -4px 0px -2px ${theme.palette.divider}`,
    transform: "translateY(-50%) translateX(-50%) rotate(45deg)",
    zIndex: 0,
  },
  triangleleft: {
    content: '""',
    display: "block",
    position: "absolute",
    top: 0,
    left: 0,
    width: triangleLength,
    height: triangleLength,
    backgroundColor: theme.palette.text.primary,
    boxShadow: `inset 4px -4px 0px -2px ${theme.palette.divider}`,
    transform: "translateY(-50%) translateX(-50%) rotate(45deg)",
    zIndex: 0,
  },
  triangleright: {
    content: '""',
    display: "block",
    position: "absolute",
    top: 0,
    left: 0,
    width: triangleLength,
    height: triangleLength,
    backgroundColor: theme.palette.text.primary,
    boxShadow: `inset -4px 4px 0px -2px ${theme.palette.divider}`,
    transform: "translateY(-50%) translateX(-50%) rotate(45deg)",
    zIndex: 0,
  },
  header: {
    color: "white",
    fontSize: 17,
    fontWeight: 600,
    marginBottom: "0.6rem",
  },
  body: {
    color: "white",
    fontSize: 16,
    fontWeight: 400,
  },
}));

// position of tooltip relative to ref element
enum Position {
  "lefttop",
  "leftcenter",
  "leftbottom",

  "topleft",
  "topcenter",
  "topright",

  "righttop",
  "rightcenter",
  "rightbottom",

  "bottomleft",
  "bottomcenter",
  "bottomright",

  "center",
}

// where on the ref element the tooltip is centered to
enum MountPosition {
  "left",
  "right",
  "top",
  "bottom",
  "centerscreen",
}

interface GuidedTourStep {
  bgColor?: string;
  header: string;
  body: string;
  /** Where on the ref element the tooltip is centered to. */
  mountPosition: MountPosition | string;
  /** Position of tooltip and arrow relative to ref element. */
  position: Position | string;
  /** DOM element ID of element the tooltip will mount to. Leave out the #. */
  targetId: string;
}

export interface GuidedTourTooltipProps {
  parentMenuOpen: boolean;
  steps: GuidedTourStep[];
  currentStep: number;
  setCurrentStep: (newValue: number) => void;
  parentId: string;
  guidedTourOpen?: boolean;
  closeGuidedTour?: () => void;
  openGuidedTour?: () => void;
  exitLinkText?: string;
  hideStepper?: boolean;
  hideRestart?: boolean;
  exitButtonText?: string;
  tooltipHeight?: number;
}

const getWidth = () =>
  window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

const getHeight = () =>
  window.innerHeight ||
  document.documentElement.clientHeight ||
  document.body.clientHeight;

const getScrollY = () => window.scrollY;

const blankRect: DOMRect = new DOMRect(0, 0, 0, 0);

export default function GuidedTourTooltip(props: GuidedTourTooltipProps) {
  const classes = useStyles(props);
  const { currentStep, setCurrentStep } = props;
  const [, setWidth] = useState(getWidth());
  const [, setHeight] = useState(getWidth());
  const [scrollY, setScrollY] = useState(getScrollY());
  const [, setRerender] = useState(false);
  const {
    exitLinkText = "Skip guided tour",
    hideStepper = false,
    hideRestart = false,
    exitButtonText = "Exit tour",
  } = props;

  const getElement = (elementId: string) => {
    return window.document.querySelector(`#${elementId}`);
  };

  const getOffsetPosition = (elementPos: DOMRect) => {
    const step = props.steps[currentStep];
    let mountXOffset = 0;
    let mountYOffset = 0;

    switch (step.mountPosition) {
      case "left":
        mountXOffset = 0 - triangleLength;
        mountYOffset = elementPos.height / 2;
        break;
      case "right":
        mountXOffset = elementPos.width + triangleLength;
        mountYOffset = elementPos.height / 2;
        break;
      case "top":
        mountXOffset = elementPos.width / 2;
        mountYOffset = 0 - triangleLength;
        break;
      case "bottom":
        mountXOffset = elementPos.width / 2;
        mountYOffset = elementPos.height + triangleLength;
        break;
      case "centerscreen":
        mountXOffset = elementPos.width / 2;
        mountYOffset = elementPos.height / 2;
        break;
    }

    let posXOffset = 0;
    let posYOffset = 0;

    switch (step.position) {
      case "lefttop":
        posYOffset = 0 + padY;
        break;
      case "leftcenter":
        posYOffset = tooltipHeight / 2;
        break;
      case "leftbottom":
        posYOffset = tooltipHeight - padY;
        break;

      case "topleft":
        posXOffset = 0 + padX;
        break;
      case "topcenter":
        posXOffset = tooltipWidth / 2;
        break;
      case "topright":
        posXOffset = tooltipWidth - padX;
        break;

      case "righttop":
        posXOffset = tooltipWidth;
        posYOffset = 0 + padY;
        break;
      case "rightcenter":
        posXOffset = tooltipWidth;
        posYOffset = tooltipHeight / 2;
        break;
      case "rightbottom":
        posXOffset = tooltipWidth;
        posYOffset = tooltipHeight - padY;
        break;

      case "bottomleft":
        posXOffset = 0 + padX;
        posYOffset = tooltipHeight;
        break;
      case "bottomcenter":
        posXOffset = tooltipWidth / 2;
        posYOffset = tooltipHeight;
        break;
      case "bottomright":
        posXOffset = tooltipWidth - padX;
        posYOffset = tooltipHeight;
        break;
      case "center":
        posXOffset = tooltipWidth / 2;
        posYOffset = tooltipHeight / 2;
        break;
    }

    // force the menu over and up if the help and resources menu is open
    let menuXOffset = 0;
    let menuYOffset = 0;
    if (
      props.parentMenuOpen &&
      step.targetId === "help-and-resources" &&
      step.mountPosition !== "centerscreen"
    ) {
      menuXOffset = -235;
      menuYOffset = -80;
    }

    const x = elementPos.x + mountXOffset - posXOffset + menuXOffset;
    const y = elementPos.y + scrollY + mountYOffset - posYOffset + menuYOffset;
    return { x, y };
  };

  const getElementPosition = (step: number) => {
    const element = getElement(props.steps[step].targetId);
    const elementPos: DOMRect = element?.getBoundingClientRect() ?? blankRect;

    const screenRect: DOMRect = new DOMRect(0, 0, window.innerWidth, window.innerHeight);

    const pos =
      props.steps[step].mountPosition === "centerscreen"
        ? getOffsetPosition(screenRect)
        : getOffsetPosition(elementPos);

    return pos;
  };

  useEffect(() => {
    const resizeListener = () => {
      setWidth(getWidth());
      setHeight(getHeight());
    };
    window.addEventListener("resize", resizeListener);
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  useEffect(() => {
    const scrollListener = () => {
      setScrollY(getScrollY());
      setRerender((prev) => !prev);
    };
    const appElement = getElement("App");
    appElement?.addEventListener("scroll", scrollListener);
    return () => {
      appElement?.removeEventListener("scroll", scrollListener);
    };
  }, []);

  useEffect(() => {
    if (props.steps[currentStep].mountPosition !== "centerscreen") {
      let screenPosition: ScrollLogicalPosition = "center";
      switch (props.steps[currentStep].mountPosition) {
        case "left":
        case "right":
          screenPosition = "center";
          break;
        case "top":
          screenPosition = "end";
          break;
        case "bottom":
          screenPosition = "start";
          break;
      }

      const element = getElement(props.steps[currentStep].targetId);
      element?.scrollIntoView({
        behavior: "smooth",
        block: screenPosition,
        inline: "nearest",
      });
    }
  }, [currentStep, props.steps]);

  const handleExitTour = () => {
    generateGtmEvent({
      event: "modal",
      action: "close",
      name: "onclick",
      type: "click",
      region: "main content",
      section: props.steps[currentStep].header.toLowerCase(),
      text: "exit tour",
    });

    if (props.closeGuidedTour) {
      props.closeGuidedTour();
    }
  };

  const handleSkipTour = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: props.steps[currentStep].header.toLowerCase(),
      text: exitLinkText,
    });

    if (props.closeGuidedTour) {
      props.closeGuidedTour();
    }
  };

  const handleNext = () => {
    generateGtmEvent({
      event: "select",
      action: "click",
      name: "onclick",
      type: "carousel",
      region: "main content",
      section: props.steps[currentStep].header.toLowerCase(),
      text: "right chevron",
    });
    let nextStep = currentStep + 1;
    const stepLength = props.steps.length;
    setCurrentStep(nextStep % stepLength);
  };

  const handlePrev = () => {
    generateGtmEvent({
      event: "select",
      action: "click",
      name: "onclick",
      type: "carousel",
      region: "main content",
      section: props.steps[currentStep].header.toLowerCase(),
      text: "left chevron",
    });
    let nextStep = currentStep - 1;
    const stepLength = props.steps.length;
    if (nextStep < 0) nextStep = stepLength - 1;

    setCurrentStep(nextStep % stepLength);
  };

  const getTriangle = () => {
    const step = props.steps[currentStep];
    const position = step.position.toString();

    let posXOffset = 0;
    let posYOffset = 0;
    switch (position) {
      case "lefttop":
        posYOffset = 0 + padY;
        break;
      case "leftcenter":
        posYOffset = tooltipHeight / 2;
        break;
      case "leftbottom":
        posYOffset = tooltipHeight - padY;
        break;

      case "topleft":
        posXOffset = padX;
        break;
      case "topcenter":
        posXOffset = tooltipWidth / 2;
        break;
      case "topright":
        posXOffset = tooltipWidth - padX;
        break;

      case "righttop":
        posXOffset = tooltipWidth;
        posYOffset = 0 + padY;
        break;
      case "rightcenter":
        posXOffset = tooltipWidth;
        posYOffset = tooltipHeight / 2;
        break;
      case "rightbottom":
        posXOffset = tooltipWidth;
        posYOffset = tooltipHeight - padY;
        break;

      case "bottomleft":
        posXOffset = 0 + padX;
        posYOffset = tooltipHeight;
        break;
      case "bottomcenter":
        posXOffset = tooltipWidth / 2;
        posYOffset = tooltipHeight;
        break;
      case "bottomright":
        posXOffset = tooltipWidth - padX;
        posYOffset = tooltipHeight;
        break;
      case "center":
        posXOffset = tooltipWidth / 2;
        posYOffset = tooltipHeight / 2;
        break;
    }

    let className = "";
    if (position.startsWith("left")) {
      className = classes.triangleleft;
    } else if (position.startsWith("top")) {
      className = classes.triangleup;
    } else if (position.startsWith("right")) {
      className = classes.triangleright;
    } else if (position.startsWith("bottom")) {
      className = classes.triangledown;
    }

    return (
      <Box
        className={classes.triangle}
        sx={{
          position: "absolute",
          left: posXOffset,
          top: posYOffset,
          zIndex: 1201,
        }}>
        <div className={className}></div>
      </Box>
    );
  };

  const position = getElementPosition(currentStep);
  const header = props.steps[currentStep].header;
  const body = props.steps[currentStep].body;

  const TourElement = (
    <Box
      className={classes.tooltip}
      sx={{
        position: "absolute",
        left: position.x,
        top: position.y,
        transition: "all 1s ease",
        zIndex: 1000,
      }}
      style={{ backgroundColor: props.steps[currentStep].bgColor }}>
      {getTriangle()}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}>
        <Box>
          <Typography className={classes.header}>{header}</Typography>
          <Typography className={classes.body}>{body}</Typography>
        </Box>
        <Box sx={{ marginTop: "auto" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={0}>
            {currentStep < props.steps.length - 1 ? (
              <>
                <Link className={classes.skipLink} onClick={handleSkipTour} tabIndex={0}>
                  {exitLinkText}
                </Link>
                <Box>
                  {currentStep > 0 && (
                    <IconButtonOutlined
                      className={classes.outlinedButton}
                      size="small"
                      onClick={handlePrev}
                      sx={{ marginRight: "0.5em" }}
                      aria-label="Previous tip">
                      <IconChevronLeft style={{ width: "16px", height: "16px" }} />
                    </IconButtonOutlined>
                  )}

                  <IconButtonOutlined
                    className={classes.outlinedButton}
                    size="small"
                    onClick={handleNext}
                    aria-label="Next tip">
                    <IconChevronRight style={{ width: "16px", height: "16px" }} />
                  </IconButtonOutlined>
                </Box>
              </>
            ) : (
              <>
                <Link
                  className={classes.skipLink}
                  onClick={() => {
                    generateGtmEvent({
                      event: "link",
                      action: "click",
                      name: "onclick",
                      type: "internal link",
                      region: "main content",
                      section: props.steps[currentStep].header.toLowerCase(),
                      text: "restart tour",
                    });
                    props.setCurrentStep(0);
                  }}
                  tabIndex={0}>
                  {!hideRestart && "Restart tour"}
                </Link>
                <Button
                  className={classes.outlinedButton}
                  variant="outlined"
                  size="small"
                  onClick={handleExitTour}
                  aria-label="Exit tour">
                  {exitButtonText}
                </Button>
              </>
            )}
          </Stack>
          {!hideStepper && (
            <Stack
              sx={{ marginTop: "1em" }}
              direction="row"
              justifyContent="center"
              alignItems="center">
              <Typography variant="body1">
                {currentStep + 1}/{props.steps.length}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );

  return TourElement;
}
